/**
 * StrExternalDeliveryModal
 *
 * Two-step flow: Upload (or select an existing document) → Confirm & Deliver.
 * When no delivery record exists, staff may either upload new files or
 * select from documents already in the client library.
 *
 * APIs used:
 *   Status:   GET  /api/str-external-delivery/client/{clientId}/status
 *   Eligible: GET  /api/str-external-delivery/client/{clientId}/eligible-documents
 *   Classify: POST /api/documents-intel/{docId}/classification/confirm
 *   Confirm:  POST /api/str-external-delivery/client/{clientId}/confirm-delivery
 *   Retry:    POST /api/str-external-delivery/client/{clientId}/retry-delivery
 *             POST /api/str-external-delivery/client/{clientId}/retry-review-request
 */

import { useCallback, useEffect, useRef, useState } from "react";
import * as Icons from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

const DOC_TYPE_STR = "strategic_tax_review";
const DOC_TYPE_ES  = "executive_summary";

const STATUS_META = {
  not_started:      { icon: "Upload",       color: "text-slate-500",   bg: "bg-slate-50",   border: "border-slate-200",  label: "No delivery record" },
  delivery_pending: { icon: "Clock",        color: "text-amber-600",   bg: "bg-amber-50",   border: "border-amber-200",  label: "Delivery Notification Pending" },
  delivered:        { icon: "CheckCircle2", color: "text-emerald-600", bg: "bg-emerald-50", border: "border-emerald-200", label: "Delivered — Awaiting Client Access" },
  client_accessed:  { icon: "Eye",          color: "text-blue-600",    bg: "bg-blue-50",    border: "border-blue-200",   label: "Client Accessed — Review Request Pending" },
  matter_complete:  { icon: "BadgeCheck",   color: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-200", label: "STR Delivery Complete" },
};

function StatusBadge({ status }) {
  const m = STATUS_META[status] || STATUS_META.not_started;
  const Ico = Icons[m.icon] || Icons.Info;
  return (
    <div className={`flex items-center gap-2 rounded-lg px-3 py-2 border text-sm font-medium ${m.color} ${m.bg} ${m.border}`}
      data-testid="str-delivery-status-badge">
      <Ico className="h-4 w-4 shrink-0" />
      {m.label}
    </div>
  );
}

// ── Library Picker ────────────────────────────────────────────────────────────
// Shows documents eligible for a specific slot type (STR or ES).

function LibraryPicker({ eligibleDocs, targetType, clientId, onSelected, onCancel }) {
  const [classifying, setClassifying] = useState(null); // doc id being classified

  const readyKey = targetType === DOC_TYPE_STR ? "str_ready" : "es_ready";
  const ready = (eligibleDocs?.[readyKey] || []);
  const candidates = (eligibleDocs?.reclassification_candidates || []);
  const classCode = targetType === DOC_TYPE_STR ? "strategic_tax_review" : "executive_summary";
  const typeLabel = targetType === DOC_TYPE_STR ? "Strategic Tax Review" : "Executive Summary";

  const handleSelectReady = (doc) => {
    onSelected({ id: doc.id, name: doc.display_filename });
  };

  const handleClassifyAndSelect = async (doc) => {
    if (classifying) return;
    setClassifying(doc.id);
    try {
      await api.post(`/documents-intel/${doc.id}/classification/confirm`, {
        code: classCode,
        reason: `Classified for STR external delivery — ${typeLabel}`,
      });
      toast.success(`Document classified as ${typeLabel}.`);
      onSelected({ id: doc.id, name: doc.display_filename });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 403) {
        toast.error(
          "You don't have permission to classify documents. Please ask an administrator to classify this document in the Document Center first, then return here to select it.",
          { duration: 8000 },
        );
      } else {
        const msg = (typeof detail === "string" ? detail : null)
          || formatApiErrorDetail(detail)
          || "Classification failed — please try again.";
        toast.error(msg);
      }
    } finally {
      setClassifying(null);
    }
  };

  const allEmpty = ready.length === 0 && candidates.length === 0;

  return (
    <div className="rounded-xl border border-blue-200 bg-blue-50/60 p-3 space-y-2" data-testid={`str-library-picker-${targetType}`}>
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold text-blue-700 uppercase tracking-wide">
          Select from Document Library — {typeLabel}
        </div>
        <button
          onClick={onCancel}
          className="text-xs text-slate-400 hover:text-slate-600 underline"
          data-testid={`str-library-cancel-${targetType}`}
        >
          Cancel
        </button>
      </div>

      {allEmpty && (
        <div className="text-xs text-slate-500 py-1">
          No eligible documents found in the library. Upload a new file instead.
        </div>
      )}

      {ready.length > 0 && (
        <div className="space-y-1">
          <div className="text-[10px] font-semibold text-slate-500 uppercase">Ready to use</div>
          {ready.map((doc) => (
            <div key={doc.id} className="flex items-center justify-between gap-2 bg-white rounded-lg border border-slate-200 px-2.5 py-1.5">
              <div className="flex items-center gap-2 min-w-0">
                <Icons.FileText className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
                <span className="text-xs text-slate-700 truncate">{doc.display_filename}</span>
                {doc.tax_year && <span className="text-[10px] text-slate-400 shrink-0">{doc.tax_year}</span>}
              </div>
              <button
                onClick={() => handleSelectReady(doc)}
                className="text-xs font-semibold text-emerald-700 bg-emerald-100 hover:bg-emerald-200 px-2 py-0.5 rounded shrink-0 transition-colors"
                data-testid={`str-library-select-ready-${doc.id}`}
              >
                Select
              </button>
            </div>
          ))}
        </div>
      )}

      {candidates.length > 0 && (
        <div className="space-y-1">
          <div className="text-[10px] font-semibold text-slate-500 uppercase">
            Needs classification first
          </div>
          {candidates.map((doc) => (
            <div key={doc.id} className="flex items-center justify-between gap-2 bg-white rounded-lg border border-amber-200 px-2.5 py-1.5">
              <div className="flex items-center gap-2 min-w-0">
                <Icons.FileText className="h-3.5 w-3.5 text-amber-500 shrink-0" />
                <span className="text-xs text-slate-700 truncate">{doc.display_filename}</span>
                {doc.tax_year && <span className="text-[10px] text-slate-400 shrink-0">{doc.tax_year}</span>}
              </div>
              <button
                onClick={() => handleClassifyAndSelect(doc)}
                disabled={!!classifying}
                className="flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-100 hover:bg-amber-200 disabled:opacity-50 px-2 py-0.5 rounded shrink-0 transition-colors"
                data-testid={`str-library-classify-select-${doc.id}`}
              >
                {classifying === doc.id
                  ? <><Icons.Loader2 className="h-3 w-3 animate-spin" />Classifying…</>
                  : <>Classify &amp; Select</>}
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Document Slot ─────────────────────────────────────────────────────────────
// Unified slot supporting both file upload and library selection.

function DocumentSlot({
  label, description, docType, clientId,
  onUploaded, uploaded, disabled,
  eligibleDocs, loadingEligible,
}) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [showPicker, setShowPicker] = useState(false);
  const [fromLibrary, setFromLibrary] = useState(false);
  const inputRef = useRef(null);

  const hasLibraryDocs = eligibleDocs && (
    (docType === DOC_TYPE_STR ? eligibleDocs.str_ready : eligibleDocs.es_ready).length > 0
    || eligibleDocs.reclassification_candidates.length > 0
  );

  const handleFile = useCallback(async (file) => {
    if (!file) return;
    setError(null);
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("destination_type", "client");
      form.append("destination_id", clientId);
      form.append("document_type", docType);
      form.append("category", "reference_material");
      form.append("upload_source", "staff_upload");
      const { data } = await api.post("/documents-intel/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const docId = data?.id || data?.document?.id;
      if (!docId) throw new Error("Upload succeeded but no document ID was returned.");
      setFromLibrary(false);
      onUploaded({ id: docId, name: file.name });
    } catch (err) {
      const msg = formatApiErrorDetail(err?.response?.data?.detail) || err?.message || "Upload failed.";
      setError(msg);
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  }, [clientId, docType, onUploaded]);

  const handleChange = (e) => { const f = e.target.files?.[0]; if (f) handleFile(f); e.target.value = ""; };
  const handleDrop = (e) => { e.preventDefault(); if (disabled || uploading) return; const f = e.dataTransfer.files?.[0]; if (f) handleFile(f); };

  const handleLibrarySelected = (doc) => {
    setError(null);       // clear any stale upload error when library doc is selected
    setFromLibrary(true);
    setShowPicker(false);
    onUploaded(doc);
  };

  const handleChange_ = () => {
    setFromLibrary(false);
    onUploaded(null);
  };

  return (
    <div
      className={`rounded-xl border-2 ${showPicker ? "border-blue-300 bg-blue-50/20" : uploaded ? "border-emerald-300 bg-emerald-50" : error ? "border-rose-300 bg-rose-50" : disabled ? "border-slate-200 bg-slate-50 opacity-60" : "border-slate-300 bg-white"} p-4 transition-colors`}
      onDragOver={(e) => { if (!showPicker) e.preventDefault(); }}
      onDrop={!showPicker ? handleDrop : undefined}
      data-testid={`str-upload-slot-${docType}`}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,.doc,.docx"
        className="sr-only"
        onChange={handleChange}
        data-testid={`str-upload-input-${docType}`}
        disabled={disabled || uploading}
      />

      <div className="flex items-start gap-3">
        <div className={`rounded-lg p-2 shrink-0 ${uploaded ? "bg-emerald-100" : error ? "bg-rose-100" : "bg-slate-100"}`}>
          {uploaded
            ? <Icons.CheckCircle2 className="h-5 w-5 text-emerald-600" />
            : error
              ? <Icons.AlertCircle className="h-5 w-5 text-rose-500" />
              : <Icons.FileUp className="h-5 w-5 text-slate-500" />}
        </div>

        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-slate-800">{label}</div>
          <div className="text-xs text-slate-500 mt-0.5">{description}</div>

          {uploaded ? (
            <div className="mt-2 space-y-1">
              <div className="flex items-center gap-1.5 text-xs text-emerald-700 font-medium" data-testid={`str-upload-filename-${docType}`}>
                <Icons.Paperclip className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{uploaded.name}</span>
                {fromLibrary && <span className="ml-1 text-[10px] bg-blue-100 text-blue-700 px-1 rounded font-semibold">Library</span>}
              </div>
              {!disabled && (
                <button
                  onClick={handleChange_}
                  className="text-xs text-slate-400 hover:text-slate-600 underline"
                  data-testid={`str-upload-replace-${docType}`}
                >
                  Change
                </button>
              )}
            </div>
          ) : uploading ? (
            <div className="mt-2 flex items-center gap-1.5 text-xs text-blue-600">
              <Icons.Loader2 className="h-3.5 w-3.5 animate-spin" />Uploading…
            </div>
          ) : (
            /* Fix 1: unified action area — shown in both default and error states so
               "Use Existing Document" is always reachable regardless of prior upload
               failures (including HTTP 409 duplicate rejection). */
            <div className="mt-2 space-y-2">
              {error && (
                <div className="flex items-center gap-1.5 text-xs text-rose-600" data-testid={`str-upload-error-${docType}`}>
                  <Icons.AlertCircle className="h-3.5 w-3.5 shrink-0" />
                  <span className="truncate">{error}</span>
                  <button
                    onClick={() => { setError(null); inputRef.current?.click(); }}
                    className="ml-1 underline shrink-0"
                    data-testid={`str-upload-retry-${docType}`}
                  >Retry</button>
                </div>
              )}
              <div className="flex flex-wrap items-center gap-2">
                <button
                  onClick={() => { setShowPicker(false); inputRef.current?.click(); }}
                  className="text-xs text-blue-600 font-medium hover:underline inline-flex items-center gap-1"
                  disabled={disabled || uploading}
                  data-testid={`str-upload-btn-${docType}`}
                >
                  <Icons.Upload className="h-3.5 w-3.5" />Upload file
                </button>
                {hasLibraryDocs && !disabled && (
                  <>
                    <span className="text-slate-300 text-xs">|</span>
                    <button
                      onClick={() => setShowPicker((v) => !v)}
                      className="text-xs text-emerald-600 font-medium hover:underline inline-flex items-center gap-1"
                      data-testid={`str-library-btn-${docType}`}
                    >
                      <Icons.FolderOpen className="h-3.5 w-3.5" />
                      {showPicker ? "Hide Library" : "Use Existing Document"}
                    </button>
                  </>
                )}
                {loadingEligible && !hasLibraryDocs && (
                  <span className="text-xs text-slate-400 flex items-center gap-1">
                    <Icons.Loader2 className="h-3 w-3 animate-spin" />Checking library…
                  </span>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {showPicker && !uploaded && (
        <div className="mt-3">
          <LibraryPicker
            eligibleDocs={eligibleDocs}
            targetType={docType}
            clientId={clientId}
            onSelected={handleLibrarySelected}
            onCancel={() => setShowPicker(false)}
          />
        </div>
      )}
    </div>
  );
}

// ── Delivery Status View ──────────────────────────────────────────────────────

function DeliveryStatusView({ record, clientId, onRetryDelivery, onRetryRR, retrying }) {
  if (!record) return null;
  const status = record.status || "not_started";
  const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }); }
    catch { return iso; }
  };

  return (
    <div className="space-y-3" data-testid="str-delivery-status-view">
      <StatusBadge status={status} />
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 space-y-2">
        <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-1">Documents Confirmed</div>
        {(record.strategic_review_doc_id_filename || record.strategic_review_doc_id) && (
          <div className="flex items-center gap-2 text-sm text-slate-700" data-testid="str-delivery-str-doc">
            <Icons.FileText className="h-4 w-4 text-slate-400 shrink-0" />
            <span className="truncate">{record.strategic_review_doc_id_filename || record.strategic_review_doc_id}</span>
            {record.strategic_review_doc_id_portal_visible && (
              <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded shrink-0">Portal</span>
            )}
          </div>
        )}
        {(record.executive_summary_doc_id_filename || record.executive_summary_doc_id) && (
          <div className="flex items-center gap-2 text-sm text-slate-700" data-testid="str-delivery-es-doc">
            <Icons.FileText className="h-4 w-4 text-slate-400 shrink-0" />
            <span className="truncate">{record.executive_summary_doc_id_filename || record.executive_summary_doc_id}</span>
            {record.executive_summary_doc_id_portal_visible && (
              <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded shrink-0">Portal</span>
            )}
          </div>
        )}
      </div>
      <div className="space-y-1.5 text-xs text-slate-500">
        {record.confirmed_at && (
          <div className="flex items-center gap-2">
            <Icons.CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
            <span>Confirmed by <span className="font-medium text-slate-700">{record.confirmed_by_name || "staff"}</span> — {fmtDate(record.confirmed_at)}</span>
          </div>
        )}
        {record.delivery_email_sent_at ? (
          <div className="flex items-center gap-2">
            <Icons.Mail className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
            <span>Delivery notification sent — {fmtDate(record.delivery_email_sent_at)}</span>
          </div>
        ) : status === "delivery_pending" && (
          <div className="flex items-center gap-2">
            <Icons.AlertCircle className="h-3.5 w-3.5 text-amber-500 shrink-0" />
            <span>Delivery notification not yet sent</span>
          </div>
        )}
        {record.first_accessed_at && (
          <div className="flex items-center gap-2">
            <Icons.Eye className="h-3.5 w-3.5 text-blue-500 shrink-0" />
            <span>Client first access — {fmtDate(record.first_accessed_at)}</span>
          </div>
        )}
        {record.review_request_sent_at && (
          <div className="flex items-center gap-2">
            <Icons.Star className="h-3.5 w-3.5 text-emerald-500 shrink-0" />
            <span>Review request sent — {fmtDate(record.review_request_sent_at)}</span>
          </div>
        )}
        {status === "client_accessed" && !record.review_request_sent_at && record.review_request_status === "failed" && (
          <div className="flex items-center gap-2">
            <Icons.AlertCircle className="h-3.5 w-3.5 text-rose-500 shrink-0" />
            <span>Review request failed — retry available</span>
          </div>
        )}
      </div>
      {status === "delivery_pending" && (
        <button onClick={onRetryDelivery} disabled={retrying}
          className="w-full inline-flex items-center justify-center gap-2 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white font-semibold rounded-xl px-4 py-2.5 text-sm transition-colors"
          data-testid="str-delivery-retry-btn">
          {retrying ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : <Icons.RefreshCw className="h-4 w-4" />}
          {retrying ? "Retrying…" : "Retry Delivery Notification"}
        </button>
      )}
      {status === "client_accessed" && !record.review_request_sent_at && (
        <button onClick={onRetryRR} disabled={retrying}
          className="w-full inline-flex items-center justify-center gap-2 bg-blue-500 hover:bg-blue-600 disabled:opacity-50 text-white font-semibold rounded-xl px-4 py-2.5 text-sm transition-colors"
          data-testid="str-delivery-retry-rr-btn">
          {retrying ? <Icons.Loader2 className="h-4 w-4 animate-spin" /> : <Icons.RefreshCw className="h-4 w-4" />}
          {retrying ? "Retrying…" : "Retry Review Request"}
        </button>
      )}
    </div>
  );
}

// ── Main Modal ────────────────────────────────────────────────────────────────

export function StrExternalDeliveryModal({ open, onClose, clientId, clientName, onDelivered }) {
  const [loadState, setLoadState] = useState("loading");
  const [deliveryRecord, setDeliveryRecord] = useState(null);
  const [strDoc, setStrDoc] = useState(null);
  const [esDoc, setEsDoc] = useState(null);
  const [confirming, setConfirming] = useState(false);
  const [retrying, setRetrying] = useState(false);
  const [eligibleDocs, setEligibleDocs] = useState(null);
  const [loadingEligible, setLoadingEligible] = useState(false);
  const [eligibleDocsError, setEligibleDocsError] = useState(null); // null | "permission_denied" | "server_error"

  const bothFilled = strDoc && esDoc;

  const load = useCallback(async () => {
    if (!clientId || !open) return;
    try {
      const { data } = await api.get(`/str-external-delivery/client/${clientId}/status`);
      setDeliveryRecord(data.delivery_record || null);
      const st = data.status || "not_started";
      if (st === "not_started") {
        setStrDoc(null);
        setEsDoc(null);
        setLoadState("upload");
        // Load eligible documents from library in parallel
        setLoadingEligible(true);
        api.get(`/str-external-delivery/client/${clientId}/eligible-documents`)
          .then(({ data: ed }) => { setEligibleDocs(ed); setEligibleDocsError(null); })
          .catch((err) => {
            setEligibleDocs(null);
            // Fix 2: surface authorization and server failures visibly
            const status = err?.response?.status;
            if (status === 403) {
              setEligibleDocsError("permission_denied");
            } else if (err) {
              setEligibleDocsError("server_error");
            }
          })
          .finally(() => setLoadingEligible(false));
      } else {
        setLoadState("status");
      }
    } catch (err) {
      console.error("StrExternalDeliveryModal: failed to load status", err);
      setLoadState("upload");
    }
  }, [clientId, open]);

  useEffect(() => {
    if (open) {
      setLoadState("loading");
      setStrDoc(null);
      setEsDoc(null);
      setConfirming(false);
      setRetrying(false);
      setEligibleDocs(null);
      setEligibleDocsError(null);
      load();
    }
  }, [open, load]);

  const handleConfirmDelivery = async () => {
    if (!bothFilled || confirming) return;
    setConfirming(true);
    try {
      const result = await api.post(`/str-external-delivery/client/${clientId}/confirm-delivery`, {
        strategic_review_doc_id: strDoc.id,
        executive_summary_doc_id: esDoc.id,
      });
      if (result.data?.status === "already_delivered") {
        toast.info("This STR has already been delivered.");
        await load();
        return;
      }
      toast.success("STR delivery initiated — client notification sent.");
      await load();
      onDelivered?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail?.status === "payment_required") {
        toast.error("Payment required before delivery can be confirmed. Please collect the outstanding balance first.");
      } else {
        const msg = (typeof detail === "string" ? detail : null)
          || formatApiErrorDetail(detail)
          || "Could not confirm delivery — please try again.";
        toast.error(msg);
      }
    } finally {
      setConfirming(false);
    }
  };

  const handleRetryDelivery = async () => {
    if (retrying) return;
    setRetrying(true);
    try {
      await api.post(`/str-external-delivery/client/${clientId}/retry-delivery`);
      toast.success("Delivery notification sent.");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Retry failed — please try again.");
    } finally {
      setRetrying(false);
    }
  };

  const handleRetryRR = async () => {
    if (retrying) return;
    setRetrying(true);
    try {
      await api.post(`/str-external-delivery/client/${clientId}/retry-review-request`);
      toast.success("Review request sent.");
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err?.response?.data?.detail) || "Retry failed — please try again.");
    } finally {
      setRetrying(false);
    }
  };

  const hasAnyLibraryDocs = eligibleDocs && (
    eligibleDocs.str_ready.length > 0 ||
    eligibleDocs.es_ready.length > 0 ||
    eligibleDocs.reclassification_candidates.length > 0
  );

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg" data-testid="str-external-delivery-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" data-testid="str-modal-title">
            <Icons.PackageCheck className="h-5 w-5 text-orange-500 shrink-0" />
            {loadState === "status" ? "STR Delivery Status" : "Upload Completed STR"}
          </DialogTitle>
          <DialogDescription className="text-slate-500">
            {loadState === "status"
              ? `External STR delivery for ${clientName || "this client"}.`
              : `STR prepared outside Tax Flow. Upload both final documents (or select from the library), then confirm delivery to ${clientName || "the client"}.`}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          {loadState === "loading" && (
            <div className="flex items-center justify-center py-8 gap-2 text-slate-500" data-testid="str-modal-loading">
              <Icons.Loader2 className="h-5 w-5 animate-spin" />
              <span className="text-sm">Loading delivery status…</span>
            </div>
          )}

          {loadState === "upload" && (
            <div className="space-y-3" data-testid="str-upload-section">
              {/* Existing documents notice */}
              {hasAnyLibraryDocs && (
                <div className="flex items-start gap-2 text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2"
                  data-testid="str-library-notice">
                  <Icons.Library className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                  <span>
                    Eligible documents found in the client library.
                    Use <strong>Use Existing Document</strong> on each slot to select without uploading.
                  </span>
                </div>
              )}

              {/* Fix 2: permission / server error notices for eligible-documents discovery */}
              {eligibleDocsError === "permission_denied" && (
                <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2"
                  data-testid="str-library-permission-error">
                  <Icons.Lock className="h-3.5 w-3.5 shrink-0" />
                  <span>You do not have permission to access the document library. Upload files directly or contact your administrator.</span>
                </div>
              )}
              {eligibleDocsError === "server_error" && (
                <div className="flex items-center gap-2 text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2"
                  data-testid="str-library-unavailable">
                  <Icons.AlertCircle className="h-3.5 w-3.5 shrink-0" />
                  <span>Document library temporarily unavailable. You can still upload new files.</span>
                </div>
              )}

              <div className="flex items-center gap-2 text-xs text-slate-500 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                <Icons.Info className="h-3.5 w-3.5 text-blue-500 shrink-0" />
                <span>Upload or select both final documents, then confirm delivery. Client notification is sent only after explicit confirmation.</span>
              </div>

              {/* STR slot */}
              <div>
                <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1.5">
                  Document 1 — Strategic Tax Review <span className="text-rose-500">*</span>
                </div>
                <DocumentSlot
                  label="Strategic Tax Review"
                  description="Final STR report — PDF or Word document"
                  docType={DOC_TYPE_STR}
                  clientId={clientId}
                  onUploaded={setStrDoc}
                  uploaded={strDoc}
                  disabled={confirming}
                  eligibleDocs={eligibleDocs}
                  loadingEligible={loadingEligible}
                />
              </div>

              {/* ES slot */}
              <div>
                <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-1.5">
                  Document 2 — Executive Summary <span className="text-rose-500">*</span>
                </div>
                {/* Fix 4: guidance when no pre-classified ES exists in the library */}
                {eligibleDocs && eligibleDocs.es_ready.length === 0 && !esDoc && (
                  <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-1.5 mb-2 flex items-center gap-1.5"
                    data-testid="str-es-missing-notice">
                    <Icons.Info className="h-3 w-3 shrink-0" />
                    <span>No pre-classified Executive Summary found in the library. Upload a separate ES document — the STR will not fulfil this requirement automatically.</span>
                  </div>
                )}
                <DocumentSlot
                  label="Executive Summary"
                  description="Executive summary of findings — PDF or Word document"
                  docType={DOC_TYPE_ES}
                  clientId={clientId}
                  onUploaded={setEsDoc}
                  uploaded={esDoc}
                  disabled={confirming}
                  eligibleDocs={eligibleDocs}
                  loadingEligible={loadingEligible}
                />
              </div>

              {/* Confirm section */}
              {bothFilled && (
                <div className="rounded-xl border border-orange-200 bg-orange-50 p-4 space-y-3" data-testid="str-confirm-section">
                  <div className="text-sm font-semibold text-slate-800">Review &amp; Confirm Delivery</div>
                  <div className="space-y-1.5">
                    <div className="flex items-center gap-2 text-xs text-slate-600" data-testid="str-confirm-str-name">
                      <Icons.FileText className="h-3.5 w-3.5 text-orange-500 shrink-0" />
                      <span className="font-medium truncate">{strDoc.name}</span>
                      <span className="text-slate-400 shrink-0">Strategic Tax Review</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-slate-600" data-testid="str-confirm-es-name">
                      <Icons.FileText className="h-3.5 w-3.5 text-orange-500 shrink-0" />
                      <span className="font-medium truncate">{esDoc.name}</span>
                      <span className="text-slate-400 shrink-0">Executive Summary</span>
                    </div>
                  </div>
                  <div className="text-xs text-slate-500 border-t border-orange-200 pt-2">
                    Both documents will be made available in the client portal and the delivery notification will be sent.
                    Payment must be settled before delivery can proceed.
                  </div>
                  <button
                    onClick={handleConfirmDelivery}
                    disabled={confirming}
                    className="w-full inline-flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-50 text-white font-semibold rounded-xl px-4 py-2.5 text-sm transition-colors"
                    data-testid="str-confirm-deliver-btn"
                  >
                    {confirming
                      ? <><Icons.Loader2 className="h-4 w-4 animate-spin" />Confirming…</>
                      : <><Icons.Send className="h-4 w-4" />Confirm &amp; Deliver to Client</>}
                  </button>
                </div>
              )}
            </div>
          )}

          {loadState === "status" && (
            <DeliveryStatusView
              record={deliveryRecord}
              clientId={clientId}
              onRetryDelivery={handleRetryDelivery}
              onRetryRR={handleRetryRR}
              retrying={retrying}
            />
          )}
        </div>

        <DialogFooter data-testid="str-modal-footer">
          <button
            onClick={onClose}
            className="text-sm px-4 py-2 rounded-lg bg-slate-900 text-white font-medium hover:bg-slate-700 transition-colors"
            data-testid="str-modal-close-btn"
          >
            {loadState === "status" ? "Close" : "Cancel"}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default StrExternalDeliveryModal;
